import Swiper from 'swiper'
import { A11y, Navigation } from 'swiper/modules'
import { initMediaGalleryZoom } from './pdp--media-gallery-zoom.js'

const LOADING_TIMEOUT_MS = 2500

function escAttr (s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
}

function slideMarkup (src, alt, isFirstSlide) {
    const s = escAttr(src)
    const a = escAttr(alt)
    const loading = isFirstSlide ? 'eager' : 'lazy'
    const fetchPriorityAttr = isFirstSlide ? ' fetchpriority="high"' : ''
    return (
        '<div class="swiper-slide"><div class="pdp-media-gallery__zoom-container">' +
        `<img src="${s}" alt="${a}" loading="${loading}"${fetchPriorityAttr} data-zoom-src="${s}">` +
        '<div class="pdp-media-gallery__zoom-result"></div></div></div>'
    )
}

function thumbMarkup (src, index, isActive) {
    const active = isActive ? ' pdp-media-gallery__thumb--active' : ''
    return (
        `<button type="button" class="pdp-media-gallery__thumb${active}" role="listitem" data-index="${index}" aria-label="View image ${index + 1}">` +
        `<img src="${escAttr(src)}" alt="" loading="lazy" width="120" height="120"></button>`
    )
}

function destroyGallerySwiper () {
    if (!window.mainSwiperInstance) return
    try {
        window.mainSwiperInstance.destroy(true, true)
    } catch {
        /* Swiper may already be torn down */
    }
    window.mainSwiperInstance = null
}

/** @param {number} count */
function setGalleryChromeVisibility (count, navEl, zoomToggleEl, thumbsWrap) {
    if (navEl) navEl.hidden = count < 2
    if (zoomToggleEl) zoomToggleEl.hidden = count === 0
    if (thumbsWrap) thumbsWrap.hidden = count === 0
}

/**
 * First main slide only (hero is eager; thumbs + other slides stay lazy).
 * @param {Element} mainCarouselEl
 */
async function waitForHeroImageReady (mainCarouselEl) {
    const img = mainCarouselEl.querySelector('.swiper-slide img')
    if (!img) return

    if (typeof img.decode === 'function') {
        try {
            await img.decode()
        } catch {
            /* ignore */
        }
    }
    if (img.complete && img.naturalHeight > 0) return

    await new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true })
        img.addEventListener('error', resolve, { once: true })
    })
}

/**
 * Swaps PDP media gallery when #product-select changes (variant metafield images in JSON).
 */
export function bindPdpMediaGalleryVariantSwitching () {
    const dataEl = document.getElementById('pdp-media-gallery-by-variant')
    const masterSelect = document.querySelector('#product-select')
    if (!dataEl || !masterSelect) return

    let byVariant
    try {
        byVariant = JSON.parse(dataEl.textContent.trim())
    } catch {
        return
    }

    const root = document.querySelector('[data-pdp-media-gallery]')
    if (!root) return

    const loadingOverlay = root.querySelector('.pdp-media-gallery__loading-overlay')
    const mainEl = root.querySelector('.pdp-media-gallery__main')
    const thumbsEl = root.querySelector('.pdp-media-gallery__thumbnails')
    const thumbsWrap = root.querySelector('.pdp-media-gallery__thumbnails-wrapper')
    if (!mainEl || !thumbsEl) return

    const wrapper = mainEl.querySelector('.swiper-wrapper')
    const navEl = mainEl.querySelector('.pdp-media-gallery__main-navigation')
    const zoomToggleEl = mainEl.querySelector('.pdp-media-gallery__zoom-toggle')
    if (!wrapper) return

    const setLoadingUi = (on) => {
        root.classList.toggle('pdp-media-gallery--loading', on)
        if (on) {
            root.setAttribute('aria-busy', 'true')
            loadingOverlay?.setAttribute('aria-hidden', 'false')
        } else {
            root.removeAttribute('aria-busy')
            loadingOverlay?.setAttribute('aria-hidden', 'true')
        }
    }

    const endLoading = () => {
        setLoadingUi(false)
        mainEl.style.minHeight = ''
        window.mainSwiperInstance?.update?.()
    }

    const applyVariantGallery = async (variantId) => {
        const images = Array.isArray(byVariant[String(variantId)])
            ? byVariant[String(variantId)]
            : []

        destroyGallerySwiper()

        if (images.length === 0) {
            endLoading()
            wrapper.innerHTML = ''
            thumbsEl.innerHTML = ''
            setGalleryChromeVisibility(0, navEl, zoomToggleEl, thumbsWrap)
            return
        }

        const lockH = Math.round(mainEl.getBoundingClientRect().height)
        setLoadingUi(true)
        if (lockH > 48) mainEl.style.minHeight = `${lockH}px`

        setGalleryChromeVisibility(images.length, navEl, zoomToggleEl, thumbsWrap)

        wrapper.innerHTML = images.map((im, i) => slideMarkup(im.src, im.alt, i === 0)).join('')
        thumbsEl.innerHTML = images.map((im, i) => thumbMarkup(im.src, i, i === 0)).join('')

        try {
            const setActiveThumb = (index) => {
                thumbsEl.querySelectorAll('.pdp-media-gallery__thumb').forEach((btn, i) => {
                    btn.classList.toggle('pdp-media-gallery__thumb--active', i === index)
                })
            }

            const nextBtn = navEl?.querySelector('.slider-navigation__next') ?? null
            const prevBtn = navEl?.querySelector('.slider-navigation__prev') ?? null
            const hasNav = Boolean(nextBtn && prevBtn && images.length > 1)

            const mainSwiper = new Swiper(mainEl, {
                navigation: hasNav
                    ? {
                        nextEl: nextBtn,
                        prevEl: prevBtn,
                        hiddenClass: 'slider-navigation--hidden',
                        disabledClass: 'slider-navigation--disabled'
                    }
                    : false,
                slidesPerView: 1,
                spaceBetween: 0,
                modules: [Navigation, A11y]
            })

            thumbsEl.querySelectorAll('.pdp-media-gallery__thumb').forEach((btn) => {
                btn.addEventListener('click', () => {
                    const idx = parseInt(btn.getAttribute('data-index'), 10)
                    if (!Number.isNaN(idx)) mainSwiper.slideTo(idx)
                })
            })

            mainSwiper.on('slideChange', () => setActiveThumb(mainSwiper.activeIndex))
            setActiveThumb(mainSwiper.activeIndex)

            window.mainSwiperInstance = mainSwiper
            initMediaGalleryZoom(mainEl, { swiper: mainSwiper })

            await Promise.race([
                waitForHeroImageReady(mainEl),
                new Promise((resolve) => setTimeout(resolve, LOADING_TIMEOUT_MS))
            ])
        } finally {
            endLoading()
        }
    }

    let lastVariantId = masterSelect.value
    masterSelect.addEventListener('change', () => {
        const id = masterSelect.value
        if (id === lastVariantId) return
        lastVariantId = id
        applyVariantGallery(id).catch(() => {})
    })
}
